import { useFrame, useThree } from '@react-three/fiber'
import { MODE_ALZADOS, MODE_FORCE_ALZADOS } from '@constants'
import { MODULAR_IKEA_DRAG_ENABLED } from '@utils/modularSnap/config'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import { useAppStore } from '@store'
import type { AnyID } from '@types'
import { useMemo, useRef } from 'react'
import { Matrix4, Vector3 } from 'three'

function resolveToolbarTarget(
  items: Map<string, any>,
  selected: Set<AnyID>,
  currentObjectID: AnyID | '',
): { id: AnyID; item: any } | null {
  const isInSelection = (id: string) =>
    Array.from(selected).some((sid) => String(sid) === String(id))

  const tryId = (id: AnyID | '' | undefined) => {
    if (!id) return null
    const item = items.get(String(id))
    if (!item || item.type !== 'linkedItem') return null
    if (!isInSelection(String(id))) return null
    return { id: id as AnyID, item }
  }

  const fromCurrent = tryId(currentObjectID)
  if (fromCurrent) return fromCurrent

  for (const sid of selected) {
    const match = tryId(sid)
    if (match) return match
  }
  return null
}

/** Proyecta la posición del módulo seleccionado a coordenadas de pantalla. */
export function ModularSectionToolbarBridge() {
  const { camera, size, gl } = useThree()
  const currentObjectID = useAppStore.use.currentObjectID()
  const isModalOpen = useAppStore.use.isModalOpen()
  const mode = useAppStore.use.mode()
  const items = useAppStore.use.items()
  const currentLayer = useAppStore.use.currentLayer()
  const selected = useAppStore((s) => s.layers[s.currentLayer].selected)
  const itemDragActive = useModularUiStore((s) => s.itemDragActive)
  const setToolbarScreenPos = useModularUiStore((s) => s.setToolbarScreenPos)

  const worldAnchor = useRef(new Vector3())
  const matrixScratch = useRef(new Matrix4())
  const projected = useRef(new Vector3())

  const target = useMemo(
    () => resolveToolbarTarget(items as Map<string, any>, selected, currentObjectID),
    [items, selected, currentObjectID],
  )

  const showToolbar =
    MODULAR_IKEA_DRAG_ENABLED &&
    !isModalOpen &&
    !itemDragActive &&
    mode !== MODE_ALZADOS &&
    mode !== MODE_FORCE_ALZADOS &&
    Boolean(target)

  useFrame(() => {
    if (!showToolbar || !target?.item?.matrix) {
      setToolbarScreenPos(null)
      return
    }

    const width = Number(target.item.width || 0)
    const height = Number(target.item.height || 0)
    const thickness = Number(target.item.thickness || 0)
    worldAnchor.current.set(width / 2, height + 260, thickness / 2)
    matrixScratch.current.copy(target.item.matrix)
    worldAnchor.current.applyMatrix4(matrixScratch.current)

    projected.current.copy(worldAnchor.current).project(camera)
    if (projected.current.z > 1) {
      setToolbarScreenPos(null)
      return
    }

    const rect = gl.domElement.getBoundingClientRect()
    setToolbarScreenPos({
      x: rect.left + (projected.current.x * 0.5 + 0.5) * size.width,
      y: rect.top + (-projected.current.y * 0.5 + 0.5) * size.height,
      targetId: target.id,
    })
  })

  return null
}
